/**
 * App Logo Component
 *
 * Centralized branding component used across the application.
 * Ensures consistent logo appearance in login pages, headers, and sidebars.
 */

import { Brain } from 'lucide-react'
import { cn } from '@/lib/utils'
import { env } from '@/config/env'
import { PRODUCTO } from '@/config/producto'

// =============================================================================
// Types
// =============================================================================

interface AppLogoProps {
  /**
   * Size variant
   * - sm: 40x40 icon, used in headers/sidebars
   * - md: 48x48 icon, default size
   * - lg: 80x80 icon, used in login pages
   */
  size?: 'sm' | 'md' | 'lg'

  /**
   * Whether to show the app name text
   */
  showName?: boolean

  /**
   * Whether to show the subtitle
   */
  showSubtitle?: boolean

  /**
   * Custom subtitle text (overrides default)
   */
  subtitle?: string

  /**
   * Layout direction
   */
  layout?: 'horizontal' | 'vertical'

  /**
   * Text color variant for different backgrounds
   */
  textVariant?: 'light' | 'dark' | 'gradient'

  /**
   * Additional className for the container
   */
  className?: string

  /**
   * Whether the icon should have the pulse glow animation
   */
  animated?: boolean
}

// =============================================================================
// Size Configuration
// =============================================================================

const sizeConfig = {
  sm: {
    container: 'w-10 h-10',
    icon: 'w-6 h-6',
    title: 'text-lg',
    subtitle: 'text-xs',
  },
  md: {
    container: 'w-12 h-12',
    icon: 'w-7 h-7',
    title: 'text-xl',
    subtitle: 'text-xs',
  },
  lg: {
    container: 'w-20 h-20',
    icon: 'w-10 h-10',
    title: 'text-2xl',
    subtitle: 'text-sm',
  },
}

// =============================================================================
// Component
// =============================================================================

export function AppLogo({
  size = 'md',
  showName = true,
  showSubtitle = true,
  subtitle = PRODUCTO.descriptor,
  layout = 'horizontal',
  textVariant = 'dark',
  className,
  animated = false,
}: AppLogoProps) {
  const config = sizeConfig[size]

  const textColorClasses = {
    light: {
      title: 'text-white',
      subtitle: 'text-white/60',
    },
    dark: {
      title: 'text-gray-900 dark:text-gray-100',
      subtitle: 'text-gray-500 dark:text-gray-400',
    },
    gradient: {
      title: 'gradient-text',
      subtitle: 'text-[var(--foreground-muted)]',
    },
  }

  const colors = textColorClasses[textVariant]

  return (
    <div
      className={cn(
        'flex items-center',
        layout === 'vertical' ? 'flex-col text-center gap-4' : 'gap-3',
        className
      )}
    >
      {/* Icon */}
      <div
        className={cn(
          config.container,
          'rounded-lg bg-gradient-to-br from-[var(--color-primary-500)] to-[var(--color-accent-500)] flex items-center justify-center flex-shrink-0 shadow-lg',
          size === 'lg' && 'rounded-full',
          animated && 'animate-pulse-glow'
        )}
      >
        <Brain className={cn(config.icon, 'text-white')} />
      </div>

      {/*
        Horizontal recorta porque el ancho es fijo, y `min-w-0` es lo que deja
        encoger al hijo de flex para que `truncate` recorte en vez de empujar.
        Vertical parte de linea: ahi `nowrap` desbordaria la pantalla.
      */}
      {(showName || showSubtitle) && (
        <div
          className={cn(
            layout === 'horizontal' ? 'min-w-0 overflow-hidden' : 'w-full'
          )}
        >
          {showName && (
            <h1
              className={cn(
                config.title,
                'font-bold',
                layout === 'horizontal' ? 'truncate' : 'break-words',
                colors.title
              )}
            >
              {env.APP_NAME}
            </h1>
          )}
          {showSubtitle && subtitle && (
            <p
              className={cn(
                config.subtitle,
                layout === 'horizontal' ? 'truncate' : 'text-balance break-words',
                colors.subtitle
              )}
            >
              {subtitle}
            </p>
          )}
        </div>
      )}
    </div>
  )
}

// =============================================================================
// Preset Variants for Common Use Cases
// =============================================================================

/**
 * Logo for login pages (large, centered, with animation)
 */
export function LoginLogo({ subtitle }: { subtitle?: string }) {
  return (
    <AppLogo
      size="lg"
      layout="vertical"
      textVariant="gradient"
      subtitle={subtitle}
      animated
    />
  )
}

/**
 * Logo for sidebar (small, horizontal, light text)
 */
export function SidebarLogo({ collapsed = false }: { collapsed?: boolean }) {
  return (
    <AppLogo
      size="sm"
      showName={!collapsed}
      showSubtitle={!collapsed}
      textVariant="light"
    />
  )
}

/**
 * Logo for headers (small, horizontal, dark text)
 */
export function HeaderLogo({ subtitle }: { subtitle?: string }) {
  return (
    <AppLogo
      size="sm"
      textVariant="dark"
      subtitle={subtitle}
    />
  )
}
